import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {refreshTokenOnPageLoad} from '@components/Login/userSlice';
import {LOGIN_PATH, UNAUTHORIZED_PATH} from '@constants/index';
import useIsPrivateRoute from '@hooks/useIsPrivateRoute';
import {useCurrentRoute} from '@hooks/useCurrentRoute';
import type {AppDispatch, RootState} from '@src/client/index';

interface UseTokenRefreshResult {
    isInitializing: boolean;
    isAuthenticated: boolean | null;
}

export const useTokenRefresh = (): UseTokenRefreshResult => {
    const dispatch = useDispatch<AppDispatch>();
    const [isInitializing, setIsInitializing] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const accessToken = useSelector((state: RootState) => state.user.accessToken);
    const {isPrivateRoute} = useIsPrivateRoute();
    const currentRoute = useCurrentRoute();
    const isSignerRoute = currentRoute?.isSignerRoute || false;

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                if (accessToken) {
                    setIsAuthenticated(true);
                    setIsInitializing(false);
                    return;
                }

                if (!isPrivateRoute) {
                    setIsAuthenticated(false);
                    setIsInitializing(false);
                    return;
                }

                const success = await dispatch(refreshTokenOnPageLoad());

                if (!success) {
                    if (isSignerRoute) {
                        window.location.href = `${UNAUTHORIZED_PATH}/recipient`;
                        return;
                    }
                    const currentPath = window.location.pathname + window.location.search;
                    const hasTarget = currentPath && currentPath !== LOGIN_PATH;
                    window.location.href = hasTarget
                        ? `${LOGIN_PATH}?target=${encodeURIComponent(currentPath)}`
                        : LOGIN_PATH;
                    return;
                }
                setIsAuthenticated(success);
            } catch (error) {
                console.error('Token refresh error:', error);
                setIsAuthenticated(false);
                if (isPrivateRoute) {
                    if (isSignerRoute) {
                        window.location.href = `${UNAUTHORIZED_PATH}/recipient`;
                        return;
                    }
                    const currentPath = window.location.pathname + window.location.search;
                    const hasTarget = currentPath && currentPath !== LOGIN_PATH;
                    window.location.href = hasTarget
                        ? `${LOGIN_PATH}?target=${encodeURIComponent(currentPath)}`
                        : LOGIN_PATH;
                }
            } finally {
                setIsInitializing(false);
            }
        };

        initializeAuth();
    }, [dispatch, accessToken]);

    return {
        isInitializing,
        isAuthenticated
    };
};
